import React from 'react';
import AgreementPageLayout from '../../components/AgreementPageLayout';

const BusinessAccountAgreementPage: React.FC = () => {
  return (
    <AgreementPageLayout
      title="AACore Business Account Agreement"
      effectiveDate="01/01/2026"
      lastUpdated="06/10/2026"
      toc={[
        { id: 'operator-contact', title: 'Operator & Contact' },
        { id: 'acceptance-authority', title: '1) Acceptance; Authority' },
        { id: 'definitions', title: '2) Definitions' },
        { id: 'trial-period', title: '3) Free Trial, Enhancement Period, and Use at Your Own Risk' },
        { id: 'platform-tool', title: '4) Platform Is a Tool; You Provide the Services' },
        { id: 'business-account-security', title: '5) Business Account; Admin Responsibilities; Security' },
        { id: 'customer-relationship', title: '6) Your Customer Relationship; Your Terms' },
        { id: 'business-data', title: '7) Business Data Ownership; License to Operate' },
        { id: 'data-protection', title: '8) Data Protection and Privacy' },
        { id: 'messaging', title: '9) Messaging, Email, SMS, and Notifications' },
        { id: 'payments', title: '10) Payments, Deposits, Stripe, and Chargebacks' },
        { id: 'taxes-records', title: '11) Taxes, Receipts, Records, and Accounting' },
        { id: 'availability-maintenance', title: '12) Availability, Maintenance, and No Uptime Commitment' },
        { id: 'disclaimers', title: '13) Disclaimers; As-Is Platform' },
        { id: 'prohibited-activities', title: '14) Prohibited Activities and Illegal Conduct' },
        { id: 'content-guidelines', title: '15) Business Profile and Content Guidelines' },
        { id: 'third-party-services', title: '16) Third-Party Services and Integrations' },
        { id: 'intellectual-property', title: '17) Intellectual Property' },
        { id: 'confidentiality-feedback', title: '18) Confidentiality and Feedback' },
        { id: 'suspension-termination', title: '19) Suspension; Termination' },
        { id: 'export-deletion-backups', title: '20) Data Export, Deletion, and Backups' },
        { id: 'indemnification', title: '21) Indemnification' },
        { id: 'limitation-liability', title: '22) Limitation of Liability' },
        { id: 'release', title: '23) Release' },
        { id: 'force-majeure', title: '24) Force Majeure' },
        { id: 'dispute-resolution', title: '25) Dispute Resolution; Arbitration; Class Action Waiver' },
        { id: 'governing-law', title: '26) Governing Law; Venue' },
        { id: 'changes-agreement', title: '27) Changes to Agreement' },
        { id: 'miscellaneous', title: '28) Miscellaneous' },
        { id: 'agreement-acknowledgment', title: 'Agreement Acknowledgment' },
      ]}
    >
      <p>
        This Agreement governs business use of AACore, including AACore Booking and related modules. It is written to
        clarify that AACore is a technology platform, not the provider of the Business&apos;s services, not a payment
        processor, and not a holder of full payment card or banking information.
      </p>

      <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <div className="font-semibold mb-1">Important Trial and Payments Notice</div>
        AACore Booking is provided during an enhancement, testing, and trial period through December 31, 2026, unless
        A&amp;A Inomatic, LLC announces a different date or paid subscription plan. During this period, access to AACore
        Booking subscription features is free, but third-party processor fees, chargebacks, taxes, communication fees,
        and other third-party costs may still apply. Businesses use the Platform at their own risk and should keep backup
        records and contingency procedures.
      </div>

      <div id="operator-contact" className="mt-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
        <div className="font-semibold text-gray-900 mb-1">Operator &amp; Contact</div>
        <strong>Platform:</strong> AACore, including AACore Booking and related modules (the &ldquo;Platform&rdquo;)
        <br />
        <strong>Operator:</strong> A&amp;A Inomatic, LLC (&ldquo;AAI,&rdquo; &ldquo;Company,&rdquo; &ldquo;we,&rdquo;
        &ldquo;us,&rdquo; &ldquo;our&rdquo;)
        <br />
        <strong>Support:</strong> <a href="mailto:support@aainomatic.com">support@aainomatic.com</a>
        <br />
        <strong>Privacy Policy:</strong> <a href="/privacy">AACore Privacy Policy</a>
        <br />
        <strong>Address:</strong> Clifton, VA 20124
        <p className="mt-3 mb-0">
          This AACore Business Account Agreement (&ldquo;Agreement&rdquo;) governs use of the Platform by the entity or
          individual registering or using a business account (&ldquo;Business,&rdquo; &ldquo;you,&rdquo; &ldquo;your&rdquo;).
        </p>
      </div>

      <h3 id="acceptance-authority">1) Acceptance; Authority</h3>
      <p>
        By creating a Business account, clicking &ldquo;I Agree,&rdquo; accepting an online acknowledgment, signing an
        order form, enabling payments, paying an invoice, accessing administrative features, or otherwise using the
        Platform, you agree to this Agreement.
      </p>
      <p>
        If you accept on behalf of an entity, you represent and warrant that you have full authority to bind that entity
        and its authorized Business Users.
      </p>
      <p>
        If you do not agree, do not create a Business account, do not enable payment features, and do not use the
        Platform.
      </p>

      <h3 id="definitions">2) Definitions</h3>
      <ul>
        <li>
          <strong>&ldquo;Business Data&rdquo;</strong> means all data, content, records, files, schedules, services,
          pricing, policies, customer information, notes, images, messages, forms, and other information submitted to or
          generated through the Platform by or for your Business.
        </li>
        <li>
          <strong>&ldquo;Customer&rdquo;</strong> means an end user, client, guest, or consumer who books, requests,
          purchases, communicates with, or receives services from your Business.
        </li>
        <li>
          <strong>&ldquo;Business Users&rdquo;</strong> means your staff, owners, contractors, representatives,
          administrators, and anyone else you authorize to access or use your Business account.
        </li>
        <li>
          <strong>&ldquo;Payment Processor&rdquo;</strong> means Stripe or another third-party payment provider used to
          process online payments, deposits, tips, refunds, payouts, disputes, chargebacks, identity verification, or
          related payment services.
        </li>
        <li>
          <strong>&ldquo;Payment Data&rdquo;</strong> means payment card numbers, CVV codes, bank account numbers,
          routing numbers, identity verification materials, payout account details, transaction records, payment tokens,
          processor account identifiers, payment status, payment intent identifiers, charge identifiers, refund
          identifiers, limited card descriptors, and related payment-processing information.
        </li>
        <li>
          <strong>&ldquo;Service Providers&rdquo;</strong> means third parties AAI uses to operate, support, secure, host,
          communicate, analyze, or improve the Platform, including hosting, database, email, SMS, analytics, logging,
          security, and payment-related vendors.
        </li>
        <li>
          <strong>&ldquo;Company Parties&rdquo;</strong> means AAI and its owners, officers, directors, managers,
          employees, contractors, agents, affiliates, licensors, vendors, and Service Providers.
        </li>
        <li>
          <strong>&ldquo;Applicable Law&rdquo;</strong> means all laws, rules, regulations, licensing requirements, tax
          obligations, privacy obligations, consumer protection rules, advertising rules, labor requirements, accessibility
          requirements, payment rules, card network rules, and industry obligations applicable to your Business, your
          services, your Business Users, your Customers, or your use of the Platform.
        </li>
      </ul>

      <h3 id="trial-period">3) Free Trial, Enhancement Period, and Use at Your Own Risk</h3>
      <p>
        AACore Booking is currently being provided as part of a free enhancement, testing, and trial period through
        December 31, 2026, unless AAI provides a different written date, transitions to a paid plan, or ends free access
        earlier or later in its discretion.
      </p>
      <p>
        During the trial period, AACore Booking subscription access is provided without a subscription charge from AAI.
        This does not waive or cover third-party fees, Payment Processor fees, chargebacks, refunds, reversals, taxes,
        communication carrier fees, pass-through costs, or other costs not expressly waived by AAI in writing.
      </p>
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
        <p className="mb-0">
          <strong>Trial risk acknowledgment.</strong> You understand that the Platform is still being enhanced and may
          contain defects, unfinished features, configuration changes, downtime, incorrect results, design changes, feature
          removals, workflow changes, or other issues. You use the Platform at your own risk and remain responsible for
          verifying bookings, payments, communications, policies, records, and customer obligations outside of the
          Platform when needed.
        </p>
      </div>
      <p>
        AAI may modify, suspend, limit, or discontinue any free, trial, beta, early access, or enhancement-period feature
        at any time. AAI has no obligation to provide credits, refunds, damages, service-level remedies, or continued free
        access.
      </p>

      <h3 id="platform-tool">4) Platform Is a Tool; You Provide the Services</h3>
      <p>
        The Platform provides scheduling, booking, customer communication, payment-connectivity, recordkeeping, and
        business management tools. AAI does not provide your services, does not employ your staff, does not supervise your
        operations, does not inspect your work, and is not responsible for the services your Business offers or performs.
      </p>
      <p>You are solely responsible for:</p>
      <ul>
        <li>service quality, outcomes, safety, sanitation, professional judgment, customer care, and fulfillment;</li>
        <li>licensing, credentials, permits, registrations, insurance, inspections, and legal compliance;</li>
        <li>hiring, training, supervision, compensation, and conduct of your Business Users;</li>
        <li>pricing, taxes, fees, tips, discounts, deposits, refunds, cancellations, no-shows, and customer policies;</li>
        <li>customer screening, intake, disclosures, consent forms, aftercare, and age or eligibility requirements;</li>
        <li>customer complaints, injuries, dissatisfaction, disputes, chargebacks, and regulatory inquiries; and</li>
        <li>verifying that the Platform configuration matches your actual business practices.</li>
      </ul>
      <p>
        <strong>No agency.</strong> Nothing in this Agreement creates a partnership, joint venture, franchise, employment,
        fiduciary, agency, representative, reseller, or broker relationship between AAI and any Business, Customer,
        Business User, or Payment Processor.
      </p>

      <h3 id="business-account-security">5) Business Account; Admin Responsibilities; Security</h3>
      <p>
        You must provide accurate business information and keep it current. You will designate at least one administrative
        user and are responsible for all activity that occurs under your account, whether authorized or unauthorized,
        except to the extent caused solely by AAI&apos;s willful misconduct.
      </p>
      <p>You are responsible for:</p>
      <ul>
        <li>controlling user access, roles, passwords, devices, and permissions;</li>
        <li>removing access promptly when staff, contractors, or representatives leave or no longer need access;</li>
        <li>ensuring Business Users use the Platform only for authorized business purposes;</li>
        <li>maintaining secure passwords, devices, browsers, networks, and recovery methods;</li>
        <li>
          reviewing account settings, payment settings, service listings, calendars, prices, taxes, and policies for
          accuracy; and
        </li>
        <li>
          notifying <a href="mailto:support@aainomatic.com">support@aainomatic.com</a> promptly if you suspect unauthorized
          access or account compromise.
        </li>
      </ul>

      <h3 id="customer-relationship">6) Your Customer Relationship; Your Terms</h3>
      <p>
        You control and are solely responsible for your relationship with Customers. You must provide, maintain, and honor
        lawful customer-facing policies, disclosures, and terms, including where applicable:
      </p>
      <ul>
        <li>cancellations, reschedules, no-show rules, late-arrival rules, deposits, and refund rules;</li>
        <li>health, safety, sanitation, allergy, medical, aftercare, or service-risk disclosures;</li>
        <li>age restrictions, guardian consent, identity requirements, and service eligibility;</li>
        <li>payment, tipping, sales tax, gratuity, surcharge, gift card, and dispute policies; and</li>
        <li>privacy notices, marketing consents, and communication preferences.</li>
      </ul>
      <p>
        If you display policies through the Platform, you are solely responsible for their content, enforceability,
        accuracy, and compliance with Applicable Law. AAI does not review your policies for legal sufficiency.
      </p>

      <h3 id="business-data">7) Business Data Ownership; License to Operate</h3>
      <p>
        You retain ownership of your Business Data. You grant AAI a limited, non-exclusive, worldwide license to host,
        process, store, transmit, display, reproduce, and use Business Data only as reasonably necessary to:
      </p>
      <ul>
        <li>operate, secure, maintain, support, and provide the Platform;</li>
        <li>process bookings, communications, forms, invoices, settings, and related records;</li>
        <li>connect your account to Service Providers and Payment Processors when enabled by you;</li>
        <li>troubleshoot, audit, prevent fraud or abuse, enforce this Agreement, and comply with law;</li>
        <li>improve Platform features, performance, security, and usability; and</li>
        <li>create aggregated or de-identified analytics that do not identify your Customers as individuals.</li>
      </ul>
      <p>
        You represent and warrant that you have all rights, permissions, notices, and legal bases required to provide
        Business Data to AAI and to allow AAI to process it as described in this Agreement.
      </p>

      <h3 id="data-protection">8) Data Protection and Privacy</h3>

      <h4>8.1 Your compliance obligations</h4>
      <p>
        You agree to comply with Applicable Law regarding privacy, customer data, communications, consumer protection,
        and records. You will provide legally required notices, obtain required consents, collect only lawful and
        appropriate information, and respond to Customer rights requests when required by law.
      </p>

      <h4>8.2 Service Providers</h4>
      <p>
        AAI may use Service Providers to operate and support the Platform. Service Providers may process Business Data
        only as needed to provide services to AAI or as otherwise permitted by law and contract.
      </p>

      <h4>8.3 Data minimization; prohibited sensitive data</h4>
      <p>Unless AAI expressly agrees in writing, you must not store, upload, or request through the Platform:</p>
      <ul>
        <li>full payment card numbers, CVV codes, magnetic stripe data, or full bank account and routing numbers;</li>
        <li>Social Security numbers, driver&apos;s license numbers, passport numbers, or government identity documents;</li>
        <li>
          biometric identifiers, precise geolocation tracking, health records, HIPAA protected health information, or
          highly regulated sensitive data;
        </li>
        <li>information about minors beyond what is lawful, necessary, and properly authorized; or</li>
        <li>any information that your Business is not legally permitted to collect or process.</li>
      </ul>
      <p>
        If you upload or collect prohibited or excessive data, you do so at your own risk and remain solely responsible
        for all resulting obligations, claims, losses, and consequences.
      </p>

      <h4>8.4 Shared security responsibility</h4>
      <p>
        AAI maintains reasonable safeguards for the Platform. You are responsible for securing your own account, devices,
        staff access, passwords, browsers, networks, and exported files. AAI is not responsible for compromise caused by
        weak passwords, malware, shared credentials, unauthorized staff use, lost devices, insecure networks, browser
        extensions, phishing, or user error on your side.
      </p>

      <h4>8.5 Incident cooperation</h4>
      <p>
        You agree to cooperate reasonably with AAI in investigating suspected security incidents, unauthorized access,
        fraud, payment disputes, or compliance issues involving your account.
      </p>

      <h3 id="messaging">9) Messaging, Email, SMS, and Notifications</h3>
      <p>
        The Platform may send transactional messages such as confirmations, reminders, status updates, account notices,
        payment notices, and schedule changes. You are responsible for the accuracy of Customer phone numbers and email
        addresses entered into the Platform.
      </p>
      <p>
        You are responsible for complying with TCPA, CAN-SPAM, carrier rules, consent requirements, opt-out requirements,
        privacy rules, and other Applicable Law for messages sent by or on behalf of your Business.
      </p>
      <p>
        If the Platform allows marketing or promotional messages, you must obtain any required consent before sending them
        and must honor opt-outs. AAI may restrict or disable messaging features for compliance risk, carrier filtering,
        spam complaints, high bounce rates, abuse, or suspected unlawful use.
      </p>
      <p>
        <strong>Delivery is not guaranteed.</strong> Message delivery may be delayed or blocked by carriers, spam filters,
        device settings, inbox rules, network outages, incorrect contact information, or third-party provider issues. AAI
        is not liable for delayed, failed, misdirected, filtered, duplicated, or undelivered messages.
      </p>

      <h3 id="payments">10) Payments, Deposits, Stripe, and Chargebacks</h3>
      <p>
        If you enable payment features, online payments, deposits, invoices, gift card purchases, tips, refunds, or
        related payment workflows, those payment services are provided by a Payment Processor such as Stripe, not by AAI.
      </p>
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
        <p className="mb-0">
          <strong>No Stripe affiliation and no banking custody.</strong> AAI is not owned by, operated by, sponsored by,
          endorsed by, affiliated with, or acting as an agent of Stripe. AAI is not a bank, payment processor, money
          transmitter, escrow agent, merchant of record, financial institution, credit provider, payment facilitator,
          payment intermediary, or fiduciary for your funds. AAI does not hold, control, receive custody of, settle, or
          guarantee Customer funds, Business payouts, deposits, refunds, reversals, or chargeback outcomes.
        </p>
      </div>

      <h4>10.1 Payment Processor terms</h4>
      <p>
        You are responsible for reviewing, accepting, and complying with the Payment Processor&apos;s terms, privacy
        notices, identity verification requirements, prohibited business rules, risk reviews, underwriting, fee schedules,
        payout rules, reserves, holds, disputes, and card network rules. Your ability to use payment features may depend
        on the Payment Processor approving, maintaining, or restricting your account.
      </p>

      <h4>10.2 No storage of full payment card or banking information</h4>
      <p>
        AAI does not store full payment card numbers, CVV codes, full bank account numbers, full routing numbers, or full
        payout banking credentials. When payment features are enabled, full payment card and banking information should
        be entered only into Payment Processor-controlled flows or hosted payment interfaces. The Platform may store or
        display limited payment-related metadata received from a Payment Processor, such as payment status, transaction
        identifiers, customer identifiers, connected account identifiers, invoice identifiers, payment intent identifiers,
        refund identifiers, limited card descriptors, timestamps, amounts, and audit records.
      </p>

      <h4>10.3 Business responsibility for payments</h4>
      <p>You are solely responsible for:</p>
      <ul>
        <li>setting accurate prices, deposits, taxes, tips, discounts, fees, and refund rules;</li>
        <li>
          confirming that payment settings, service settings, invoices, gift cards, and deposit requirements are correct
          before making them available to Customers;
        </li>
        <li>issuing refunds, honoring lawful refund policies, responding to disputes, and handling chargebacks;</li>
        <li>maintaining sufficient funds for refunds, reversals, disputes, processor fees, and chargebacks;</li>
        <li>paying any processor fees, platform fees, taxes, assessments, penalties, and pass-through costs;</li>
        <li>recordkeeping, tax reporting, sales tax, income tax, Form 1099 or similar reporting, and accounting; and</li>
        <li>ensuring your products, services, business type, and transactions are permitted by the Payment Processor and Applicable Law.</li>
      </ul>

      <h4>10.4 Holds, reserves, processor downtime, and disputes</h4>
      <p>
        Payment Processors may delay, hold, reverse, reserve, reject, suspend, or terminate payments or payouts for reasons
        outside AAI&apos;s control. AAI is not responsible for processor downtime, payment failures, duplicate charges,
        declined cards, failed refunds, held funds, delayed payouts, reserves, identity verification failures, account
        closures, dispute outcomes, or chargeback decisions.
      </p>

      <h4>10.5 Suspension of payment features</h4>
      <p>
        AAI may suspend, disable, or limit payment-related features if AAI detects or suspects fraud, excessive disputes,
        unusual activity, unlawful services, prohibited business activity, abuse, misconfiguration, processor
        restrictions, security risks, or violation of this Agreement.
      </p>

      <h3 id="taxes-records">11) Taxes, Receipts, Records, and Accounting</h3>
      <p>
        You are solely responsible for determining, collecting, reporting, and remitting all taxes, fees, surcharges,
        duties, assessments, and similar obligations arising from your services, sales, gift cards, tips, deposits,
        refunds, payouts, or transactions.
      </p>
      <p>
        The Platform may provide invoices, records, reports, exports, or payment-status information for convenience only.
        You are responsible for maintaining your own official records, reconciling payment processor reports, and
        verifying all amounts before relying on them for accounting, tax, payroll, legal, or business decisions.
      </p>

      <h3 id="availability-maintenance">12) Availability, Maintenance, and No Uptime Commitment</h3>
      <p>
        The Platform may be unavailable, slow, incomplete, inaccurate, or interrupted due to maintenance, upgrades, bugs,
        capacity limits, data migration, security work, hosting issues, internet outages, communication provider issues,
        Payment Processor outages, or other events outside AAI&apos;s control.
      </p>
      <p>
        AAI does not guarantee uninterrupted service, error-free service, data availability, message delivery, payment
        availability, booking availability, or any specific uptime percentage unless a separate written agreement signed
        by AAI expressly states otherwise.
      </p>
      <p>
        You are responsible for maintaining backup procedures and manual alternatives appropriate for your Business,
        including offline calendars, customer contact lists, payment reconciliation methods, and appointment confirmation
        processes.
      </p>

      <h3 id="disclaimers">13) Disclaimers; As-Is Platform</h3>
      <p>
        To the maximum extent permitted by law, the Platform, including AACore Booking, is provided &ldquo;as is,&rdquo;
        &ldquo;as available,&rdquo; &ldquo;with all faults,&rdquo; and &ldquo;at your own risk.&rdquo;
      </p>
      <p>
        AAI disclaims all warranties, whether express, implied, statutory, or otherwise, including warranties of
        merchantability, fitness for a particular purpose, title, non-infringement, accuracy, reliability, availability,
        security, uninterrupted use, error-free operation, compatibility, and any warranty arising from course of dealing or
        usage of trade.
      </p>
      <p>
        AAI does not warrant that the Platform will meet your requirements, comply with laws applicable to your specific
        Business, prevent missed appointments, prevent double-booking, prevent payment disputes, prevent user error,
        generate accurate taxes, produce legally sufficient customer policies, or satisfy any particular business outcome.
      </p>
      <p>
        AAI is not responsible for lost revenue, lost profits, lost customers, lost goodwill, reputational harm, business
        interruption, missed appointments, failed communications, payment failures, service defects, customer
        dissatisfaction, staff misconduct, or user configuration errors.
      </p>

      <h3 id="prohibited-activities">14) Prohibited Activities and Illegal Conduct</h3>
      <p>You and your Business Users must not use the Platform to:</p>
      <ul>
        <li>
          violate any law, regulation, license requirement, payment rule, consumer protection rule, privacy rule, labor
          rule, tax rule, advertising rule, or accessibility rule;
        </li>
        <li>offer illegal, unsafe, deceptive, discriminatory, harmful, fraudulent, or prohibited products or services;</li>
        <li>misrepresent prices, discounts, deposits, policies, availability, credentials, reviews, or business identity;</li>
        <li>transmit spam, unlawful marketing, harassing messages, malware, phishing attempts, or deceptive communications;</li>
        <li>
          upload, display, or distribute content that is unlawful, infringing, defamatory, obscene, hateful, threatening,
          discriminatory, or invasive of privacy;
        </li>
        <li>scrape, reverse engineer, resell, sublicense, copy, disrupt, overload, probe, bypass, or interfere with the Platform;</li>
        <li>
          attempt unauthorized access to accounts, systems, source code, databases, credentials, or non-public Platform
          information;
        </li>
        <li>circumvent required fees, processor rules, security controls, rate limits, access controls, or enforcement actions; or</li>
        <li>
          use the Platform in a way that creates risk for AAI, other businesses, Customers, Service Providers, Payment
          Processors, or Platform stability.
        </li>
      </ul>
      <p>
        AAI may remove content, restrict features, suspend accounts, terminate accounts, preserve evidence, cooperate with
        Service Providers or Payment Processors, and report suspected illegal activity to authorities where appropriate.
      </p>

      <h3 id="content-guidelines">15) Business Profile and Content Guidelines</h3>
      <p>
        You are responsible for all content displayed, uploaded, entered, imported, or sent through the Platform,
        including business names, short names, URLs, addresses, service descriptions, prices, images, logos, policies,
        instructions, forms, messages, promotions, and Customer-facing content.
      </p>
      <p>
        Your content must be accurate, lawful, non-deceptive, non-infringing, respectful, and compliant with Applicable
        Law. You must have all rights needed for logos, photos, images, trademarks, names, descriptions, service lists,
        and other materials you upload.
      </p>
      <p>
        AAI may moderate, remove, restrict, or refuse content in its discretion, including content that creates legal,
        reputational, payment, security, or platform risk.
      </p>

      <h3 id="third-party-services">16) Third-Party Services and Integrations</h3>
      <p>
        The Platform may depend on or connect to third-party services, including hosting providers, email providers, SMS
        providers, analytics tools, maps, browsers, app stores, device operating systems, Payment Processors, and other
        integrations.
      </p>
      <p>
        Third-party services are controlled by their own providers and may be subject to separate terms, fees, restrictions,
        outages, changes, and privacy practices. AAI is not responsible for third-party services, third-party content,
        third-party outages, third-party pricing, third-party decisions, third-party data practices, or your compliance
        with third-party terms.
      </p>

      <h3 id="intellectual-property">17) Intellectual Property</h3>
      <p>
        AAI owns all rights, title, and interest in and to the Platform, including software, user interfaces, designs,
        workflows, documentation, databases, code, trade secrets, trademarks, service marks, domain names, templates,
        reports, analytics, know-how, and related intellectual property.
      </p>
      <p>
        During the term of your account, AAI grants you a limited, revocable, non-exclusive, non-transferable,
        non-sublicensable license to access and use the Platform for your internal business operations, subject to this
        Agreement.
      </p>
      <p>
        You may not copy, resell, lease, sublicense, distribute, modify, create derivative works of, reverse engineer,
        decompile, benchmark publicly, or exploit the Platform except as expressly permitted by law or by written
        permission from AAI.
      </p>

      <h3 id="confidentiality-feedback">18) Confidentiality and Feedback</h3>
      <p>
        You agree not to disclose AAI confidential information, including non-public pricing, security details, roadmap
        information, technical design, workflows, screenshots of non-public features, account configuration methods, beta
        features, or system information, except to authorized Business Users who need to know and are bound by appropriate
        confidentiality obligations.
      </p>
      <p>
        If you provide suggestions, feedback, ideas, enhancement requests, or recommendations, AAI may use them without
        restriction, payment, attribution, or obligation to you, unless a separate written agreement signed by AAI states
        otherwise.
      </p>

      <h3 id="suspension-termination">19) Suspension; Termination</h3>
      <p>AAI may suspend, limit, disable, or terminate your account or specific features immediately if:</p>
      <ul>
        <li>you violate this Agreement or Applicable Law;</li>
        <li>your use creates legal, payment, security, reputational, operational, or compliance risk;</li>
        <li>fraud, abuse, excessive chargebacks, illegal activity, or unauthorized access is suspected;</li>
        <li>
          a Service Provider, Payment Processor, court, regulator, or law enforcement request requires or encourages action;
        </li>
        <li>
          your account information, payment configuration, business identity, or service activity appears inaccurate or
          misleading; or
        </li>
        <li>your use harms AAI, Customers, other Businesses, Service Providers, Payment Processors, or Platform stability.</li>
      </ul>
      <p>
        You may stop using the Platform at any time. Fees paid to AAI are non-refundable except where required by law or
        expressly stated in writing by AAI. The end of a free trial, suspension, termination, or feature change does not
        entitle you to damages, credits, free services, or reimbursement from AAI.
      </p>

      <h3 id="export-deletion-backups">20) Data Export, Deletion, and Backups</h3>
      <p>
        Where available, AAI may provide export tools or reasonable export assistance. You are responsible for exporting and
        preserving your Business Data before closing your account or discontinuing use.
      </p>
      <p>
        AAI may retain or delete Business Data after termination or inactivity according to its operational, backup, legal,
        security, dispute, audit, and compliance needs. Backups are maintained for continuity and disaster recovery and
        are not guaranteed archives for your Business.
      </p>
      <p>
        AAI is not responsible for Business Data loss caused by your deletion, account settings, staff activity, weak
        security, failure to export, third-party failures, trial changes, or termination under this Agreement.
      </p>

      <h3 id="indemnification">21) Indemnification</h3>
      <p>
        You agree to defend, indemnify, and hold harmless Company Parties from and against all claims, demands, actions,
        investigations, damages, liabilities, fines, penalties, losses, settlements, costs, and expenses, including
        reasonable attorneys&apos; fees, arising out of or related to:
      </p>
      <ul>
        <li>
          your services, products, business operations, customer interactions, injuries, dissatisfaction, negligence,
          malpractice, omissions, or service outcomes;
        </li>
        <li>
          your Business Users&apos; acts, omissions, access, conduct, credentials, employment status, compensation, or
          compliance;
        </li>
        <li>
          your Customer policies, pricing, taxes, deposits, refunds, gift cards, tips, fees, advertisements, descriptions,
          images, and content;
        </li>
        <li>
          payment disputes, refunds, reversals, chargebacks, processor fees, payout issues, taxes, identity verification,
          prohibited business activity, or Payment Processor actions;
        </li>
        <li>
          your violation of Applicable Law, third-party terms, card network rules, privacy rules, TCPA, CAN-SPAM, licensing
          rules, labor rules, accessibility rules, consumer protection rules, or tax obligations;
        </li>
        <li>
          disputes between you and Customers, staff, contractors, owners, partners, vendors, Payment Processors, or other
          third parties;
        </li>
        <li>
          your Business Data, content, intellectual property infringement, privacy violations, or improper collection or use
          of sensitive data;
        </li>
        <li>
          your misuse of the Platform, account compromise caused by your side, security failures, misconfiguration, user
          error, or unauthorized staff use; and
        </li>
        <li>your breach of this Agreement or representations made to AAI.</li>
      </ul>

      <h3 id="limitation-liability">22) Limitation of Liability</h3>
      <p>
        To the maximum extent permitted by law, Company Parties will not be liable for indirect, incidental, special,
        consequential, exemplary, punitive, enhanced, or similar damages, including lost profits, lost revenue, lost
        customers, lost goodwill, reputational harm, loss of data, business interruption, service interruption, missed
        appointments, failed messages, payment failure, chargebacks, refunds, processor holds, or cost of substitute
        services, even if advised of the possibility of such damages.
      </p>
      <p>
        To the maximum extent permitted by law, Company Parties are not liable for outages, defects, trial-period
        limitations, beta or enhancement-period issues, third-party failures, Payment Processor actions, data loss,
        security incidents not caused solely by AAI&apos;s willful misconduct, Customer disputes, user error, configuration
        mistakes, incorrect pricing, incorrect taxes, missed appointments, or communications failures.
      </p>
      <p>
        AAI&apos;s total aggregate liability for all claims related to the Platform or this Agreement will not exceed the
        greater of: (a) the fees you paid directly to AAI for the Platform during the three (3) months before the event
        giving rise to the claim; or (b) one hundred dollars ($100), unless a higher amount is required by law and cannot
        be limited by contract.
      </p>
      <p>
        For free, trial, beta, early access, or enhancement-period use, you acknowledge that AAI is providing access
        without subscription charge and that this limitation of liability is a fundamental condition of that free access.
      </p>

      <h3 id="release">23) Release</h3>
      <p>To the maximum extent permitted by law, you release Company Parties from claims arising out of or related to:</p>
      <ul>
        <li>your services, products, operations, staff, Customers, policies, pricing, taxes, and business decisions;</li>
        <li>Customer complaints, injuries, cancellations, refunds, disputes, chargebacks, and dissatisfaction;</li>
        <li>
          Payment Processor actions, Stripe-related account actions, processor downtime, withheld funds, payout delays,
          reversals, reserves, identity verification, and dispute outcomes;
        </li>
        <li>
          third-party outages involving hosting, internet, email, SMS, carriers, browsers, devices, operating systems, app
          stores, maps, Payment Processors, or other providers; and
        </li>
        <li>
          your configuration decisions, data entry, user error, staff actions, credentials, devices, and failure to maintain
          backup records.
        </li>
      </ul>

      <h3 id="force-majeure">24) Force Majeure</h3>
      <p>
        AAI is not responsible for delays, failures, data loss, unavailability, or inability to perform caused by events
        beyond its reasonable control, including natural disasters, fire, flood, weather, power failures, internet outages,
        carrier outages, hosting failures, Payment Processor failures, cyberattacks, malware, pandemics, war, terrorism,
        civil unrest, labor disputes, supply chain issues, government actions, legal restrictions, emergencies, or vendor
        failures.
      </p>

      <h3 id="dispute-resolution">25) Dispute Resolution; Arbitration; Class Action Waiver</h3>
      <p>
        <strong>Informal first.</strong> Before filing a claim, you agree to contact{' '}
        <a href="mailto:support@aainomatic.com">support@aainomatic.com</a> and allow thirty (30) days for AAI to attempt
        to resolve the matter informally.
      </p>
      <p>
        <strong>Arbitration.</strong> Except for small claims matters or claims for injunctive relief, disputes arising
        from or related to this Agreement or the Platform will be resolved by binding individual arbitration in Virginia or
        remotely by video where appropriate, under applicable arbitration rules.
      </p>
      <p>
        <strong>Class action waiver.</strong> Disputes must be brought individually and not as a class, collective,
        consolidated, private attorney general, or representative action, to the maximum extent permitted by law.
      </p>
      <p>
        <strong>Injunctive relief.</strong> Either party may seek injunctive or equitable relief in court for intellectual
        property misuse, confidentiality violations, security threats, unauthorized access, payment fraud, or misuse of
        the Platform.
      </p>

      <h3 id="governing-law">26) Governing Law; Venue</h3>
      <p>
        Virginia law governs this Agreement and any dispute related to the Platform, excluding conflict-of-law rules. If a
        dispute is not subject to arbitration, it will be brought exclusively in the state or federal courts located in
        Virginia, and you consent to personal jurisdiction and venue in those courts.
      </p>

      <h3 id="changes-agreement">27) Changes to Agreement</h3>
      <p>
        AAI may update this Agreement from time to time. Continued use of the Platform after updates become effective
        constitutes acceptance of the updated Agreement. If changes are material, AAI will provide reasonable notice through
        the Platform, email, account notice, or other reasonable method.
      </p>
      <p>
        If you do not agree to an updated Agreement, you must stop using the Platform and disable any features you no longer
        accept, including payment-related features.
      </p>

      <h3 id="miscellaneous">28) Miscellaneous</h3>
      <p>
        <strong>Assignment.</strong> You may not assign this Agreement without AAI&apos;s prior written consent. AAI may
        assign this Agreement in connection with a merger, acquisition, reorganization, financing, sale of assets,
        corporate transaction, change of control, or transfer of the Platform.
      </p>
      <p>
        <strong>Severability.</strong> If any provision is unenforceable, the remaining provisions will remain in effect,
        and the unenforceable provision will be modified to the minimum extent necessary to make it enforceable where
        possible.
      </p>
      <p>
        <strong>No waiver.</strong> AAI&apos;s failure to enforce a provision is not a waiver of its right to enforce that
        provision later.
      </p>
      <p>
        <strong>Headings.</strong> Headings are for convenience only and do not limit the Agreement.
      </p>
      <p>
        <strong>Entire agreement.</strong> This Agreement supersedes prior discussions and is the complete agreement
        regarding your Business use of the Platform, except for any signed written order, addendum, or separate agreement
        that expressly modifies this Agreement.
      </p>

      <div id="agreement-acknowledgment" className="rounded-lg border border-gray-200 bg-blue-50 px-4 py-3 text-sm text-gray-700 mt-8">
        <h3 className="mt-0">Agreement Acknowledgment</h3>
        <p>
          I agree to the AACore Business Account Agreement and certify that I am authorized to bind the Business. I
          acknowledge that AACore is a technology platform provided &ldquo;as is&rdquo; and &ldquo;as available,&rdquo;
          that AACore Booking is free during the enhancement and trial period through December 31, 2026 unless changed by
          AAI, and that I use the Platform at my own risk.
        </p>
        <p className="mb-0">
          I further acknowledge that AAI is not Stripe, is not affiliated with or endorsed by Stripe, is not a bank or
          payment processor, does not hold full payment card or banking information, does not control Customer funds or
          Business payouts, and is not responsible for my services, staff, licenses, taxes, pricing, refunds, chargebacks,
          customer policies, or legal compliance.
        </p>
      </div>
    </AgreementPageLayout>
  );
};

export default BusinessAccountAgreementPage;
